import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, BookOpen, Lock, Sparkles, ListChecks, Pencil, CheckCircle2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import HomeworkPanel from "@/components/HomeworkPanel";
import TestPanel from "@/components/TestPanel";
import StudentBookTab from "@/components/StudentBookTab";
import StudyPackUpsellCard from "@/components/StudyPackUpsellCard";
import { markClassworkComplete } from "@/lib/rewards";
import { toast } from "sonner";
import {
  PAYMENT_LABELS,
  STATUS_LABELS,
  paymentBadgeClass,
  statusBadgeClass,
  studentCourseFull,
} from "@/lib/enrollments";

export default function StudentCourseView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(0);
  const [payDialog, setPayDialog] = useState(false);

  useEffect(() => {
    studentCourseFull(id)
      .then(setData)
      .catch((err) => {
        const code = err?.response?.data?.detail;
        setError(
          code === "not_enrolled"
            ? "You aren't enrolled in this course. Contact admin."
            : code === "course_not_found"
              ? "Course not found."
              : code || "Failed to load."
        );
      });
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50">
        <DashboardHeader subtitle="Student · Course" />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <Card className="border-red-200">
            <CardContent className="py-10 text-center space-y-3">
              <div className="text-sm text-red-700" data-testid="course-error">
                {error}
              </div>
              <Button variant="outline" onClick={() => navigate("/student")}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-500">
        Loading…
      </div>
    );
  }

  const canView = data.can_view_content;
  const chapter = data.chapters[active];

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="student-course-view">
      <DashboardHeader subtitle="Student · Course" />
      <main className="mx-auto max-w-6xl px-6 py-6 space-y-4">
        <BackButton to="/student" label="My courses" testId="back-to-student" />
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-amber-600" />
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="course-title">
                {data.title}
              </h1>
            </div>
            <p className="text-sm text-stone-500 mt-1">
              Taught by <span className="font-medium">{data.teacher_name}</span> ·
              {" "}
              {data.subtitle || data.description?.slice(0, 100)}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className={statusBadgeClass(data.enrollment_status)}>
                {STATUS_LABELS[data.enrollment_status]}
              </Badge>
              <Badge variant="outline" className={paymentBadgeClass(data.payment_status)}>
                {PAYMENT_LABELS[data.payment_status]}
              </Badge>
            </div>
          </div>
        </div>

        {!canView && (
          <Card
            className="border-amber-200 bg-amber-50"
            data-testid="course-locked-banner"
          >
            <CardContent className="py-4 flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-900">
                    Awaiting payment — full content is locked
                  </div>
                  <div className="text-xs text-amber-800">
                    You can see chapter titles, but lesson notes, homework and
                    tests unlock once your first payment is recorded.
                  </div>
                </div>              </div>
              <Button
                onClick={() => setPayDialog(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="complete-payment-button"
              >
                Complete payment
              </Button>
            </CardContent>
          </Card>
        )}

        {canView && data.payment_status === "unpaid" && (
          <Card
            className="border-amber-300 bg-amber-50"
            data-testid="free-preview-banner"
          >
            <CardContent className="py-3 flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-amber-700 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-900">
                    Free preview — please complete payment to keep learning
                  </div>
                  <div className="text-xs text-amber-800">
                    Your teacher has enabled early access. Full progress,
                    certificates and study packs unlock once you pay.
                  </div>
                </div>
              </div>
              <Button
                onClick={() => setPayDialog(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="free-preview-pay-now"
              >
                Pay now
              </Button>
            </CardContent>
          </Card>
        )}

        {canView && (
          <StudyPackUpsellCard
            enrollmentId={data.enrollment_id}
            courseId={id}
          />
        )}

        <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
          {/* Chapter list */}
          <Card className="border-stone-200 h-fit">
            <CardHeader>
              <CardTitle className="text-base">
                Chapters ({data.chapters.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.chapters.map((c, i) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`w-full text-left px-4 py-3 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 ${
                    i === active ? "bg-amber-50/60" : ""
                  }`}
                  data-testid={`chapter-tab-${i}`}
                >
                  <div className="text-xs text-stone-500">Chapter {i + 1}</div>
                  <div className="text-sm font-medium truncate">{c.title}</div>
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Chapter detail */}
          <Card className="border-stone-200" data-testid="chapter-detail">
            <CardHeader>
              <CardTitle className="text-base">{chapter.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 text-sm">
              {!canView ? (
                <div className="py-12 text-center" data-testid="chapter-locked">
                  <Lock className="mx-auto h-6 w-6 text-stone-400 mb-2" />
                  <p className="text-sm text-stone-600 max-w-md mx-auto">
                    Complete your first payment to unlock the lesson notes,
                    homework and tests for this chapter.
                  </p>
                </div>
              ) : (
                <Tabs defaultValue="lesson">
                  <TabsList>
                    <TabsTrigger value="lesson" data-testid="tab-lesson">
                      Lesson
                    </TabsTrigger>
                    <TabsTrigger value="homework" data-testid="tab-homework">
                      Homework
                    </TabsTrigger>
                    <TabsTrigger value="test" data-testid="tab-test">
                      Test
                    </TabsTrigger>
                    <TabsTrigger value="book" data-testid="tab-book">
                      Reference Book
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="lesson" className="space-y-5 pt-4">
                  {chapter.learning_objectives.length > 0 && (
                    <section>
                      <h3 className="text-xs font-semibold uppercase text-stone-500 mb-2">
                        Learning objectives
                      </h3>
                      <ul className="list-disc pl-5 space-y-1">
                        {chapter.learning_objectives.map((o, i) => (
                          <li key={i}>{o}</li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {chapter.lesson_notes_markdown && (
                    <section>
                      <h3 className="text-xs font-semibold uppercase text-stone-500 mb-2 inline-flex items-center gap-1">
                        <Sparkles className="h-3 w-3" /> Lesson notes
                      </h3>
                      <pre className="whitespace-pre-wrap text-stone-800 bg-stone-50 border border-stone-200 p-3 rounded text-xs">
                        {chapter.lesson_notes_markdown}
                      </pre>
                    </section>
                  )}
                  {chapter.examples?.length > 0 && (
                    <section>
                      <h3 className="text-xs font-semibold uppercase text-stone-500 mb-2">
                        Examples
                      </h3>
                      <ul className="list-disc pl-5 space-y-1">
                        {chapter.examples.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </section>
                  )}
                  <div className="pt-2 flex justify-end">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          await markClassworkComplete(chapter.id);
                          window.dispatchEvent(
                            new CustomEvent("kks:rewards-refresh")
                          );
                        } catch (e) {
                          toast.error(
                            e?.response?.data?.detail || "Could not mark"
                          );
                        }
                      }}
                      data-testid="mark-classwork-complete"
                    >
                      <CheckCircle2 className="mr-1 h-4 w-4" /> I've finished
                      this chapter's classwork
                    </Button>
                  </div>
                  </TabsContent>
                  <TabsContent value="homework" className="pt-4">
                    <HomeworkPanel
                      courseId={id}
                      chapter={chapter}
                      canView={canView}
                    />
                  </TabsContent>
                  <TabsContent value="test" className="pt-4">
                    <TestPanel
                      courseId={id}
                      chapter={chapter}
                      canView={canView}
                    />
                  </TabsContent>
                  <TabsContent value="book" className="pt-4">
                    <StudentBookTab courseId={id} chapter={chapter} />
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Payment placeholder dialog */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment coming in Phase 5</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">
            Stripe & Razorpay flows aren't wired yet. For now, ask the admin to
            mark your payment in the dashboard so your content unlocks.
          </p>
          <DialogFooter>
            <Button onClick={() => setPayDialog(false)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
